import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { api } from '@/api/client';
import type { Page } from '@/api/types';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import s from './AdminLayout.module.css';
import { PhoneBar } from './kit/Kit';

/** 仓库列表接口接受的权限（REPO/src/app/api/warehouses/route.ts GET） */
export const WAREHOUSE_READ = ['inventory.read', 'inventory.transactions.read', 'inventory.in.write', 'inventory.out.write', 'inventory.adjust.write', 'purchase.read', 'purchase.create', 'purchase.edit', 'purchase.receive', 'orders.create', 'orders.edit.own_unshipped', 'warehouses.write'];

export type SettingsEntry = {
  key: string;
  label: string;
  to: string;
  icon: string;
  tone?: string;
  group: '设置' | '账号' | '审计';
  perms: string[];
  /** 计数用的 GET：数组取长度，分页取 total */
  countPath?: string;
};

/** 设置目录：桌面 / iPad 的设置侧栏，手机的根页入口组（system-*.html Y1） */
export const SETTINGS_ENTRIES: SettingsEntry[] = [
  { key: 'basic', label: '基本设置', to: '/settings', icon: 'set', group: '设置', perms: ['system.settings.read'] },
  { key: 'assembly', label: '组装线与工人', to: '/settings/assembly', icon: 'merge', tone: 'violet', group: '设置', perms: ['system.settings.read', 'system.settings.write'], countPath: '/api/assembly-lines' },
  { key: 'carriers', label: '物流公司', to: '/settings/carriers', icon: 'truck', tone: 'violet', group: '设置', perms: ['system.settings.read', 'system.logistics.write'], countPath: '/api/carrier-companies?pageSize=1' },
  { key: 'shops', label: '店铺名称', to: '/settings/shops', icon: 'store', tone: 'orange', group: '设置', perms: ['system.settings.read', 'system.shops.write'], countPath: '/api/shops' },
  { key: 'warehouses', label: '仓库', to: '/settings/warehouses', icon: 'home', tone: 'amber', group: '设置', perms: WAREHOUSE_READ, countPath: '/api/warehouses?pageSize=1' },
  { key: 'roles', label: '角色', to: '/users?tab=roles', icon: 'set', tone: 'blue', group: '账号', perms: ['roles.read'], countPath: '/api/roles' },
  { key: 'accounts', label: '账号', to: '/users?tab=accounts', icon: 'team', tone: 'blue', group: '账号', perms: ['users.read'], countPath: '/api/users' },
  { key: 'history', label: '权限变更记录', to: '/users/history', icon: 'hist', group: '账号', perms: ['audit.read', 'roles.read'], countPath: '/api/audit-logs?action=ROLE_PERMISSIONS_UPDATE&pageSize=1' },
  { key: 'migration', label: '迁移对照', to: '/users/migration-preview', icon: 'alert', tone: 'amber', group: '账号', perms: ['roles.read'] },
  { key: 'audit', label: '操作日志', to: '/audit-logs', icon: 'hist', group: '审计', perms: ['audit.read'], countPath: '/api/audit-logs?pageSize=1' },
  { key: 'imports', label: '导入导出历史', to: '/imports', icon: 'up', group: '审计', perms: ['imports.read'], countPath: '/api/import/jobs?pageSize=1' },
];

/** 各入口的计数（有权限才请求；失败就不显示数字） */
export function useSettingsCounts(enabled: boolean) {
  const can = useSession((st) => st.can);
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!enabled) return;
    const c = new AbortController();
    SETTINGS_ENTRIES.filter((e) => e.countPath && can(...e.perms)).forEach((e) => {
      api
        .get<unknown[] | Page<unknown>>(e.countPath!, undefined, c.signal)
        .then((d) => {
          const count = Array.isArray(d)
            ? d.length
            : typeof d === 'object' && d !== null && 'users' in d && Array.isArray(d.users)
              ? d.users.length
              : (d as Page<unknown>).pagination.total;
          setCounts((m) => ({ ...m, [e.key]: count }));
        })
        .catch(() => undefined);
    });
    return () => c.abort();
  }, [enabled, can]);
  return counts;
}

export function compactCount(n?: number) {
  if (n == null) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(n);
}

/**
 * 系统设置的外框：桌面 / iPad 在 rail 右侧留一条设置侧栏（设置不是列表而是导航，所以保留侧栏，system-ipad.html Y1）；
 * 手机没有侧栏，由根页「系统设置」的入口组导航（system-mobile.html Y1）。
 */
export function AdminLayout() {
  const ff = useFormFactor();
  const location = useLocation();
  const rolesIndexPage = location.pathname === '/users';
  const permissionHistoryPage = location.pathname === '/users/history';
  const migrationPreviewPage = location.pathname === '/users/migration-preview';
  const settingsResourcePage = location.pathname.startsWith('/settings/') || location.pathname === '/audit-logs' || location.pathname === '/imports' || rolesIndexPage;
  const compactPermissionPage = location.pathname === '/users/compare' || location.pathname.startsWith('/users/roles/') || permissionHistoryPage || location.pathname === '/users/migration-preview';
  const selectedUserTab = new URLSearchParams(location.search).get('tab') || 'roles';
  const can = useSession((st) => st.can);
  const counts = useSettingsCounts(ff !== 'phone');
  if (ff === 'phone') {
    if (permissionHistoryPage || migrationPreviewPage) {
      const phoneTabs = [
        can('roles.read') && { label: '角色', to: '/users?tab=roles' },
        can('users.read') && { label: '账号', to: '/users?tab=accounts' },
        can('audit.read') && can('roles.read') && { label: '变更记录', to: '/users/history' },
        can('roles.read') && { label: '迁移对照', to: '/users/migration-preview' },
      ].filter(Boolean) as { label: string; to: string }[];
      return <div className={s.phonePermissions}>
        <PhoneBar
          back="/apps"
          backLabel="应用"
          title="账号权限"
          right={permissionHistoryPage && can('audit.read') && can('roles.read') ? <a className={s.phoneHistoryExport} href="/api/v2/audit-logs/export?action=ROLE_PERMISSIONS_UPDATE" download="权限变更记录.csv" aria-label="导出权限变更记录"><Icon name="down" size={20} /></a> : undefined}
        />
        <nav className={s.phoneTabs} aria-label="账号权限">
          {phoneTabs.map((tab) => <NavLink key={tab.to} to={tab.to} end aria-current={location.pathname === (tab.to.split('?')[0]) ? 'page' : undefined} className={({ isActive }) => isActive ? s.phoneTabOn : ''}>{tab.label}</NavLink>)}
        </nav>
        <div className={s.phoneContent}><Outlet /></div>
      </div>;
    }
    return <Outlet />;
  }
  const entries = SETTINGS_ENTRIES.filter((e) => can(...e.perms) && (!rolesIndexPage || e.group === '账号'));
  const groups = (['设置', '账号', '审计'] as const).map((g) => ({ g, items: entries.filter((e) => e.group === g) })).filter((x) => x.items.length);
  return (
    <div className={`${s.frame} ${rolesIndexPage ? s.rolesIndexFrame : ''} ${settingsResourcePage ? s.resourceFrame : ''} ${ff === 'tablet' && settingsResourcePage && !rolesIndexPage ? s.tabletSettingsFrame : ''}`}>
      {!compactPermissionPage && <nav className={`${s.side} ${settingsResourcePage ? s.resourceSide : rolesIndexPage ? s.rolesIndexSide : ''}`} aria-label="系统设置">
        <h2>系统设置</h2>
        {groups.map(({ g, items }) => (
          <section key={g}>
            {!rolesIndexPage && <h3>{g}</h3>}
            {items.map((e) => (
              <NavLink key={e.key} to={e.to} end={e.key === 'basic'} className={({ isActive }) => {
                const userIndexActive = rolesIndexPage && (e.key === 'roles' ? selectedUserTab === 'roles' : e.key === 'accounts' ? selectedUserTab === 'accounts' : false);
                return `${s.item} ${(e.key === 'roles' || e.key === 'accounts' ? userIndexActive : isActive) ? s.on : ''}`;
              }} title={e.label}>
                <Icon name={e.icon} />
                <span>{e.label}</span>
                {counts[e.key] != null && <i className="tnum">{compactCount(counts[e.key])}</i>}
              </NavLink>
            ))}
          </section>
        ))}
      </nav>}
      <div className={s.main}><Outlet /></div>
    </div>
  );
}
